import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Copy, FileIcon } from '@codegrade/icons';
import { Icon } from '@/components/Icon';
import * as utils from '@/utils';
import './style.less';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function isButtonSize(value) {
    return typeof value === 'string' && ['small', 'medium', 'large'].includes(value);
}
const ICON_SCALES = Object.freeze({
    small: 3,
    medium: 4,
    large: 6,
});
const FILE_ICON_SCALES = Object.freeze({
    small: 1.5,
    medium: 2,
    large: 2,
});
const LIST_ICON_SCALES = Object.freeze({
    small: 2,
    medium: 3,
    large: 3,
});
export default tsx.componentFactoryOf().create({
    name: 'wizard-button',
    functional: true,
    props: {
        icon: p.ofType().required,
        /**
         * Whether to render the icon as a "file icon", meaning that the icon
         * given in the "icon" prop will be rendered smaller at the center of a
         * large "file-o" icon.
         */
        isFileIcon: p(Boolean).default(false),
        isListIcon: p(Boolean).default(false),
        label: p(String).default(''),
        size: p(String).default('medium'),
        popover: p(String).default(''),
        disabled: p(Boolean).default(false),
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render(h, ctx) {
        if (!isButtonSize(ctx.props.size)) {
            return utils.AssertionError.assert(false, `Invalid button size: ${ctx.props.size}`);
        }
        const slots = ctx.slots();
        let icon;
        if (ctx.props.isFileIcon) {
            icon = h('div', {
                class: 'file-icon',
            }, [
                <Icon icon={FileIcon} scale={ICON_SCALES[ctx.props.size]}/>,
                <Icon icon={ctx.props.icon} scale={FILE_ICON_SCALES[ctx.props.size]} class="center"/>,
            ]);
        }
        else if (ctx.props.isListIcon) {
            icon = h('div', {
                class: 'list-icon',
            }, [
                h(Icon, {
                    attrs: {
                        icon: Copy,
                        scale: ICON_SCALES[ctx.props.size],
                    },
                }),
                h(Icon, {
                    attrs: {
                        icon: ctx.props.icon,
                        scale: LIST_ICON_SCALES[ctx.props.size],
                    },
                    class: `center`,
                }),
            ]);
        }
        else {
            icon = h(Icon, {
                attrs: {
                    icon: ctx.props.icon,
                    scale: ICON_SCALES[ctx.props.size],
                },
            });
        }
        const label = (<div class={ctx.props.size === 'small' ? 'pt-1' : 'pt-2'}>
                {slots.default}
                {ctx.props.label}
            </div>);
        if (ctx.props.disabled) {
            if (ctx.data.attrs == null) {
                ctx.data.attrs = {};
            }
            ctx.data.attrs.disabled = 'disabled';
        }
        const wizardBtn = h('b-button', {
            attrs: ctx.data.attrs,
            class: {
                'wizard-button': true,
                [ctx.props.size]: true,
            },
            // Pass all event listeners on to the button, not the wrapper div.
            on: ctx.listeners,
        }, [icon, label]);
        const staticClass = ctx.data.staticClass;
        const wrapperArgs = {
            class: `wizard-button-container d-inline-block ${staticClass ?? ''} ${ctx.data.class ?? ''}`,
            style: [ctx.data.staticStyle, ctx.data.style],
        };
        if (ctx.props.popover) {
            wrapperArgs.directives = [
                {
                    name: 'b-popover',
                    value: ctx.props.popover,
                    modifiers: {
                        top: true,
                        hover: true,
                    },
                },
            ];
        }
        else if (slots.popover != null) {
            const popoverTarget = utils.coerceToString(utils.getUniqueId());
            return h('div', { ...wrapperArgs, attrs: { id: popoverTarget } }, [
                h('b-popover', { props: { target: popoverTarget, triggers: 'hover', placement: 'top' } }, [slots.popover]),
                wizardBtn,
            ]);
        }
        return h('div', wrapperArgs, [wizardBtn]);
    },
});
